import { useState } from 'react';
import type { Schema } from '../api/client';
import { ApiError } from '../api/session';
import { Button, Field, Icon, Search, Table } from '../ui';
import {
  AdminConfirm,
  AdminForm,
  AdminLink,
  AdminSelect,
  Missing,
  kindLabels,
  text,
  useAdminAction,
  useAdminNavigate,
  useAdministrative,
  useAdministrativeMe,
} from './common';
import { AdministrativeList } from './list-layout';

export function AdministrativeSections({ id, operation }: { id?: string; operation?: string }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const [remove, setRemove] = useState<Schema<'AdminSection'> | null>(null);
  const [search, setSearch] = useState('');
  const section = me.sections.find((item) => item.id === id);
  const creating = id === 'new';
  if (id && !creating && !section) return <Missing />;
  if (creating || operation === 'edit') return <SectionForm key={id} section={section} />;
  if (section)
    return (
      <>
        <header className="admin-heading">
          <h1>{section.name}</h1>
          <div className="admin-toolbar">
            {section.allowedActions.includes('section.edit') && (
              <AdminLink path={`sections/${section.id}/edit`} className="pill">
                Изменить
              </AdminLink>
            )}
            {section.allowedActions.includes('sheet.create') && (
              <AdminLink path={`sheets/new?sectionId=${section.id}`} className="pill">
                Добавить лист
              </AdminLink>
            )}
            {section.allowedActions.includes('section.delete') && (
              <Button onClick={() => setRemove(section)}>Удалить раздел</Button>
            )}
          </div>
        </header>
        <div className="admin-card-list">
          {me.sheets
            .filter((sheet) => sheet.sectionId === section.id)
            .map((sheet) => (
              <AdminLink
                className="panel admin-sheet-card"
                path={`sheets/${sheet.id}`}
                key={sheet.id}
              >
                <h2>{sheet.name}</h2>
                <p className="muted">{kindLabels[sheet.kind]}</p>
              </AdminLink>
            ))}
        </div>
        {remove && (
          <AdminConfirm
            title="Удалить раздел?"
            action={action}
            onClose={() => setRemove(null)}
            onConfirm={async () => {
              if (
                await action.run(
                  'DELETE',
                  '/api/v1/administrative/sections/{id}',
                  { version: remove.version },
                  { id: remove.id },
                  'Раздел удалён',
                )
              )
                await navigate('sections');
            }}
          >
            <p>
              Удаление доступно после переноса или удаления связанных листов и ресурсов. История
              оплат сохраняется.
            </p>
          </AdminConfirm>
        )}
      </>
    );
  return (
    <AdministrativeList
      count={me.sections.length}
      className="admin-section-list"
      toolbar={
        <>
          <Search value={search} onChange={setSearch} placeholder="Поиск разделов" />
          <span className="toolbar-spacer" />
          {me.allowedActions.includes('section.create') && (
            <AdminLink path="sections/new" className="pill admin-add-action">
              <Icon name="Plus" size={16} />
              Создать раздел
            </AdminLink>
          )}
        </>
      }
    >
      <Table
        rows={me.sections.filter((row) =>
          row.name.toLocaleLowerCase('ru').includes(search.toLocaleLowerCase('ru')),
        )}
        columns={[
          {
            key: 'name',
            label: 'Название',
            render: (row) => <AdminLink path={`sections/${row.id}`}>{row.name}</AdminLink>,
          },
          {
            key: 'template',
            label: 'Шаблон',
            render: (row) => (row.template === 'TECHNICAL' ? 'Технический' : 'Прокси'),
          },
        ]}
      />
    </AdministrativeList>
  );
}
function SectionForm({ section }: { section?: Schema<'AdminSection'> }) {
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const current = useAdministrativeMe();
  const [snapshot, setSnapshot] = useState(section);
  const [name, setName] = useState(section?.name || '');
  return (
    <AdminForm
      title={snapshot ? 'Изменить раздел' : 'Создать раздел'}
      action={action}
      cancel={snapshot ? `sections/${snapshot.id}` : 'sections'}
      submit={async (data) => {
        const result = await action.run<Schema<'AdminSection'>>(
          snapshot ? 'PATCH' : 'POST',
          snapshot ? '/api/v1/administrative/sections/{id}' : '/api/v1/administrative/sections',
          {
            name: text(data, 'name'),
            ...(snapshot ? { version: snapshot.version } : { template: text(data, 'template') }),
          },
          snapshot ? { id: snapshot.id } : {},
        );
        if (result) await navigate(`sections/${result.id}`);
      }}
    >
      {section &&
        (section.version !== snapshot?.version ||
          (action.error instanceof ApiError && action.error.status === 409)) && (
          <div role="status">
            <p>Другой сотрудник изменил запись. Ваши введённые значения сохранены.</p>
            <Button
              disabled={action.pending}
              onClick={async () => {
                const result = await current.refetch();
                const latest =
                  !result.error && result.data?.sections.find((item) => item.id === section.id);
                if (latest) {
                  setSnapshot(latest);
                  setName(latest.name);
                  action.setError(null);
                }
              }}
            >
              Загрузить новую версию
            </Button>
          </div>
        )}
      <Field
        label="Название"
        name="name"
        required
        maxLength={200}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      {!snapshot && (
        <AdminSelect
          label="Шаблон"
          name="template"
          required
          options={[
            { id: 'TECHNICAL', name: 'Технический: серверы, домены, сервисы' },
            { id: 'PROXY', name: 'Прокси' },
          ]}
        />
      )}
    </AdminForm>
  );
}
export function AdministrativeSheetForm({ id, sectionId }: { id: string; sectionId?: string }) {
  return <SheetForm key={`${id}:${sectionId || ''}`} id={id} sectionId={sectionId} />;
}
function SheetForm({ id, sectionId }: { id: string; sectionId?: string }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const sheet = me.sheets.find((item) => item.id === id);
  const current = useAdministrativeMe();
  const [snapshot, setSnapshot] = useState(sheet);
  const [name, setName] = useState(sheet?.name || '');
  const creating = id === 'new';
  const [columns, setColumns] = useState(sheet?.customColumns || []);
  const [remove, setRemove] = useState(false);
  const [resolution, setResolution] = useState('TRANSFER');
  const [replacementId, setReplacement] = useState('');
  if (!creating && !sheet) return <Missing />;
  return (
    <>
      <AdminForm
        title={creating ? 'Добавить лист' : 'Изменить лист'}
        action={action}
        cancel={sheet ? `sheets/${sheet.id}` : sectionId ? `sections/${sectionId}` : 'sections'}
        submit={async (data) => {
          const result = await action.run<Schema<'AdminSheet'>>(
            creating ? 'POST' : 'PATCH',
            creating ? '/api/v1/administrative/sheets' : '/api/v1/administrative/sheets/{id}',
            {
              name: text(data, 'name'),
              customColumns: columns,
              ...(creating
                ? { sectionId: text(data, 'sectionId'), kind: text(data, 'kind') }
                : { version: snapshot!.version }),
            },
            sheet ? { id: sheet.id } : {},
          );
          if (result) await navigate(`sheets/${result.id}`);
        }}
      >
        {sheet &&
          (sheet.version !== snapshot?.version ||
            (action.error instanceof ApiError && action.error.status === 409)) && (
            <div role="status">
              <p>Другой сотрудник изменил запись. Ваши введённые значения сохранены.</p>
              <Button
                disabled={action.pending}
                onClick={async () => {
                  const result = await current.refetch();
                  const latest =
                    !result.error && result.data?.sheets.find((item) => item.id === sheet.id);
                  if (latest) {
                    setSnapshot(latest);
                    setName(latest.name);
                    setColumns(latest.customColumns);
                    setRemove(false);
                    action.setError(null);
                  }
                }}
              >
                Загрузить новую версию
              </Button>
            </div>
          )}
        <Field
          label="Название"
          name="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={200}
        />
        {creating && (
          <>
            <AdminSelect
              name="sectionId"
              label="Раздел"
              options={me.sections}
              value={sectionId}
              required
            />
            <AdminSelect
              name="kind"
              label="Тип ресурсов"
              options={Object.entries(kindLabels).map(([id, name]) => ({ id, name }))}
              required
            />
          </>
        )}
        <h2>Дополнительные текстовые колонки</h2>
        {columns.map((column, index) => (
          <div className="admin-column-editor" key={index}>
            <Field
              label="Название"
              value={column.name}
              maxLength={200}
              required
              onChange={(e) =>
                setColumns(
                  columns.map((item, i) =>
                    i === index ? { ...item, name: e.target.value } : item,
                  ),
                )
              }
            />
            <Field
              label="Ключ колонки"
              value={column.key}
              pattern="[a-zA-Z][a-zA-Z0-9_]{0,49}"
              required
              onChange={(e) =>
                setColumns(
                  columns.map((item, i) => (i === index ? { ...item, key: e.target.value } : item)),
                )
              }
            />
            <Button onClick={() => setColumns(columns.filter((_, i) => i !== index))}>
              Убрать
            </Button>
          </div>
        ))}
        <Button
          disabled={columns.length >= 50}
          onClick={() =>
            setColumns([...columns, { key: `column${columns.length + 1}`, name: '', type: 'TEXT' }])
          }
        >
          Добавить колонку
        </Button>
      </AdminForm>
      {sheet?.allowedActions.includes('sheet.delete') && (
        <div className="admin-separated">
          <Button onClick={() => setRemove(true)}>Удалить лист</Button>
        </div>
      )}
      {remove && sheet && (
        <AdminConfirm
          title="Удалить лист?"
          action={action}
          onClose={() => setRemove(false)}
          onConfirm={async () => {
            if (
              await action.run(
                'DELETE',
                '/api/v1/administrative/sheets/{id}',
                {
                  version: snapshot!.version,
                  resolution,
                  ...(resolution === 'TRANSFER' ? { replacementId } : {}),
                },
                { id: sheet.id },
                'Лист удалён',
              )
            )
              await navigate(`sections/${sheet.sectionId}`);
          }}
        >
          <p>Выберите, что делать с ресурсами этого листа.</p>
          <AdminSelect
            label="Действие"
            options={[
              { id: 'TRANSFER', name: 'Перенести в другой лист' },
              { id: 'TRASH', name: 'Переместить в корзину' },
            ]}
            value={resolution}
            onChange={setResolution}
          />
          {resolution === 'TRANSFER' && (
            <AdminSelect
              label="Новый лист"
              options={me.sheets.filter((item) => item.id !== sheet.id && item.kind === sheet.kind)}
              value={replacementId}
              onChange={setReplacement}
              required
            />
          )}
        </AdminConfirm>
      )}
    </>
  );
}
